/* eslint-disable object-curly-newline */
/* eslint-disable no-use-before-define */
/**
 * If you are not familiar with React Navigation, refer to the 'Fundamentals' guide:
 * https://reactnavigation.org/docs/getting-started
 *
 */
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createDrawerNavigator } from '@react-navigation/drawer';
import {
  NavigationContainer, DefaultTheme, useNavigationContainerRef,
} from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ActivityIndicator, AppState, ColorSchemeName, Dimensions, Linking, Platform, StyleSheet, Text,
  View,
} from 'react-native';
import * as Application from 'expo-application';
import { SafeAreaProvider, useSafeAreaInsets } from 'react-native-safe-area-context';
import * as Device from 'expo-device';
import { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Auth } from '@aws-amplify/auth';
// eslint-disable-next-line import/no-extraneous-dependencies
import { AmplifyClass, Hub } from '@aws-amplify/core';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Notifications from 'expo-notifications';
import * as ExpoLinking from 'expo-linking';
import * as Animatable from 'react-native-animatable';
import { addNetworkStateListener } from 'expo-network';
import Colors from '../constants/Colors';
import NotFoundScreen from '../screens/NotFoundScreen';
import HomeScreen from '../screens/HomeScreen';
import { RootStackParamList, RootTabParamList } from '../types';
import LinkingConfiguration, { AppScreen } from './LinkingConfiguration';
import AnalyticsScreen from '../screens/Analytics';
import CustomDrawer from '../components/CustomDrawer';
import OccupantsScreen from '../screens/OccupantsScreen';
import AmenitiesScreen from '../screens/AmenitiesScreen';
import ErgonomicsScreen from '../screens/ErgonomicsScreen';
import SignInScreen from '../screens/SignInScreen';
import CustomHeader from '../components/CustomHeader';
import HomeIcon from '../components/HomeIcon';
import BellIcon from '../components/BellIcon';
import InternalEnvironmentScreen from '../screens/InternalEnvironment';
import ExternalEnvironmentScreen from '../screens/ExternalEnvironmentScreen';
import MapScreen from '../screens/MapScreen';
import ITScreen from '../screens/ITScreen';
import EnergyScreen from '../screens/Energy';
import SatisfactionScreen from '../screens/SatisfactionScreen';
import TicketsScreen from '../screens/Tickets';
import TicketsV2 from '../screens/TicketsV2';
import 'react-native-reanimated';
import FlagIcon from '../components/FlagIcon';
import UserMgmtScreen from '../screens/UserMgmtScreen';
import MenuScreen from '../screens/MenuScreen';
import MenuIcon from '../components/16pxIcons/MenuIcon';
import TemplateMgmtScreenMobile from '../screens/AssetMgmtScreenMobile';
import ProfileScreen from '../screens/ProfileScreen';
import ChatbotScreen from '../screens/ChatBotScreen';
import ChatbotScreenV2 from '../screens/Chatbot';
import LocationManagementScreen from '../screens/LocationManagement';
import NotificationsLogScreen from '../screens/NotificationsLogScreen';
import MapScreenMobile from '../screens/MapScreenMobile';
import NotificationsCard from '../components/NotificationsCard';
import getClientDetails from '../requests/HappyScore/GetClientDetails';
import SolveIcon24 from '../components/24pxIcons/SolveIcon24';
import GridIcon from '../components/24pxIcons/GridIcon';
import WarningIcon from '../components/60pxIcons/WarningIcon';
import AlertModal from '../components/AlertModal';
import { getCategoryScreen } from '../components/NotificationsLog/data';
import KnowledgeCenterScreen from '../screens/KnowledgeCenterScreen';
import SupportScreen from '../screens/SupportScreen';
import { updateBadgeCount, updateBuildingId, updateSelectedBuilding } from '../redux/selectedBuildingSlice';
import getSupportedVersions from '../requests/SignIn/GetSupportedVersions';
import UpdateAppScreen from '../screens/UpdateAppScreen';
import { updateHappyEnabled } from '../redux/happyEnabledSlice';
import refreshToken from '../components/RefreshToken';
import WidgetShowcaseScreen from '../screens/WidgetShowcaseScreen';
import { HappyScoreCategory, HappyScoreCategoryTitles, UserPermission } from '../constants/Types';
import { updateSelectedTicket } from '../redux/selectedTicketSlice';
import DashboardScreen from '../screens/DashboardScreen';
import GraphPrototype from '../screens/GraphPrototype';
import GetEntireLocationStructure from '../components/GetEntireLocationStructure';
import { updateLocations } from '../redux/locationStructureSlice';
import {
  setChatbotEnabled,
  setPermissions, updateIsAdmin, updateTermsAccepted, updateUsername,
} from '../redux/userSlice';
import AssetManagementScreen from '../screens/AssetManagement';
import { updateEmptyState } from '../redux/emptyStateSlice';
import TemplateMgmtScreenV2 from '../screens/TemplateManagement';
import getTicket from '../requests/Tickets/GetTicket';
import getMyUser from '../requests/Users/GetMyUser';
import TestWebView from '../screens/TestWebView';
import PDFViewerScreen from '../screens/PDFViewerScreen';
import UserMgmtScreenV2 from '../screens/UserMgmtScreenV2';
import { getCurrentLiveDateRange, refreshLiveDateRange } from '../redux/liveDateRangeSlice';
import { DateRange, updateDateRange } from '../redux/dateRangeSlice';
import { updateDateComparisonRange } from '../redux/dateComparisonRangeSlice';
import { getUserFullName } from '../constants/Utils';
import GetUnreadNotificationsCount from '../requests/Notifications/GetUnreadNotificationsCount';

export default function Navigation({
  // eslint-disable-next-line no-unused-vars
  colorScheme,
}: {
  colorScheme: ColorSchemeName;
}) {
  const runLocal = false;
  const [isUserLoggedIn, setUserLoggedIn] = useState(runLocal ? 'loggedIn' : 'initializing');
  const [authModalVisible, setAuthModalVisible] = useState<boolean>(false);
  const [ticketErrorModalVisible, setTicketErrorModalVisible] = useState<boolean>(false);
  const [ticketErrorMessage, setTicketErrorMessage] = useState<string>('');
  const notificationIdentifierMemo = useRef<Record<string, boolean>>({}).current;
  const [appStateStatus, setAppStateStatus] = useState(AppState.currentState);

  const setAlertMessage = (message: string) => {
    setTicketErrorMessage(message);
    setTicketErrorModalVisible(true);
  };

  const Amplify = new AmplifyClass();
  const AuthenticationStack = createNativeStackNavigator();
  const navigationRef = useNavigationContainerRef();
  const routeNameRef = useRef('');
  const setLocations = (data) => dispatch(updateLocations(data));
  const buildings: object = useSelector((state: { locations }) => state.locations.buildings);
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => (state.dateRange));
  const compareRange: DateRange = useSelector((state:
    { dateComparisonRange: DateRange }) => (state.dateComparisonRange));
  const setEmptyState = (value: boolean) => dispatch(updateEmptyState(value));

  // let myTheme = 'dark';

  useEffect(() => {
    Linking.addEventListener('url', () => checkAuthState());

    checkAuthState();
  }, [isUserLoggedIn]);

  useEffect(() => {
    if (!(Object.keys(buildings ?? {}).length)) return;
    dispatch(updateSelectedBuilding(
      buildings[selectedBuildingId] ?? buildings[Object.keys(buildings)[0]],
    ));
  }, [buildings]);

  const updateDateRanges: () => void = () => {
    dispatch(refreshLiveDateRange());
    if (range.selectedRange === 7) dispatch(updateDateRange(getCurrentLiveDateRange()));
    if (compareRange.selectedRange === 7) {
      dispatch(updateDateComparisonRange(getCurrentLiveDateRange()));
    }
  };

  useEffect(() => {
    Hub.listen('auth', ({ payload: { event } }) => {
      switch (event) {
        case 'signIn':
          checkAuthState();
          break;
        case 'cognitoHostedUI':
          break;
        case 'signIn_failure':
        case 'cognitoHostedUI_failure':
        case 'signOut':
          AsyncStorage.removeItem('forceHappyEnabled');
          dispatch(updateHappyEnabled(false));
          setUserLoggedIn('loggedOut');
          break;
        default:
          break;
      }
    });
  }, []);

  useEffect(() => {
    const appStateListener = AppState.addEventListener('change', setAppStateStatus);
    return () => appStateListener.remove();
  }, []);

  useEffect(() => {
    if (appStateStatus !== 'active') return () => { };
    updateDateRanges();
    const interval = setInterval(updateDateRanges, 30000);
    return () => clearInterval(interval);
  }, [range.selectedRange, compareRange.selectedRange, appStateStatus]);

  // useEffect(() => {
  //   updateTheme();
  // }, []);

  // async function updateTheme() {
  //   AsyncStorage.getItem('theme').then((theme) => {
  //     myTheme = theme ?? 'dark';
  //   });
  // }
  const dispatch = useDispatch();

  async function checkAuthState() {
    try {
      const supportedVersions = await getSupportedVersions().catch(() => { });
      if (supportedVersions?.details) {
        if (Platform.OS === 'ios') {
          if (Application.nativeBuildVersion
            < supportedVersions?.details?.ios?.minimumRequiredVersion) {
            // setUserLoggedIn('unsupportedVersion');
            // return;
          }
        } else if (Platform.OS === 'android') {
          if (Application.nativeBuildVersion
            < supportedVersions?.details?.android?.minimumRequiredVersion) {
            setUserLoggedIn('unsupportedVersion');
            return;
          }
        }
      }
      await Auth.currentAuthenticatedUser()
        .then(() => {
          Auth.currentSession().then(async (res) => {
            // const accessToken = res.getAccessToken();
            // const jwt = accessToken.getJwtToken();
            const idToken = res.getIdToken();
            // eslint-disable-next-line no-console
            console.log(JSON.stringify(idToken.payload));
            const { email } = idToken.payload;
            await AsyncStorage.setItem('token', idToken.getJwtToken());
            AsyncStorage.setItem('changePasswordEnabled', 'true');
            const clientDetailsResponse = await getClientDetails();
            const myUser = await getMyUser();
            // console.log('myUser', myUser);
            if (!myUser?.isError) {
              const forceHappyEnabled = (await AsyncStorage.getItem('forceHappyEnabled'))
                === 'enabled';
              let happyEnabled = (clientDetailsResponse?.clientDetails?.enabled_modules ?? []).includes('happy');

              // eslint-disable-next-line no-undef
              if (window?.location?.href?.includes('happyEnabled=true')) {
                happyEnabled = true; // force happy
              }
              if (forceHappyEnabled) happyEnabled = true;
              AsyncStorage.setItem('happyEnabled', happyEnabled ? 'enabled' : 'notenabled');

              dispatch(updateHappyEnabled(happyEnabled));
              AsyncStorage.setItem('email', email);
              dispatch(
                updateUsername({ firstName: myUser?.firstName, lastName: myUser?.lastName }),
              );
              const isAdmin: boolean = myUser?.isAdmin;
              dispatch(updateIsAdmin(isAdmin));
              if (!isAdmin) {
                dispatch(setPermissions(
                  myUser?.roleList.flatMap((role) => role.permissions) ?? [],
                ));
              }
              dispatch(
                setChatbotEnabled(
                  clientDetailsResponse?.clientDetails?.clientFeatures?.chatbot?.enabled ?? false,
                ),
              );
              await AsyncStorage.setItem('name', getUserFullName(myUser));
              dispatch(updateTermsAccepted(myUser?.acceptedTermsAndConditionsAt));
              // setEmptyState(undefined);
              GetEntireLocationStructure(setLocations, setEmptyState);
              // Test loading animation
              // setTimeout(() => {
              //   GetEntireLocationStructure(setLocations, setEmptyState);
              // }, 5000);
              setUserLoggedIn('loggedIn');
            } else {
              setAuthModalVisible(true);
              setUserLoggedIn('loggedOut');
            }
          });
        })
        .catch(() => {
          if (!runLocal) {
            setUserLoggedIn('loggedOut');
          }
        });
    } catch (err) {
      // setAuthModalVisible(true);
      if (!runLocal) {
        setUserLoggedIn('loggedOut');
      }
    }
  }

  useEffect(() => {
    if (Platform.OS !== 'web' && appStateStatus === 'active' && isUserLoggedIn === 'loggedIn') {
      const currentConfig = Amplify.configure() as any;
      if (currentConfig?.Auth) {
        refreshToken();
      }
    }

    const interval = setInterval(() => {
      if (Platform.OS !== 'web' && appStateStatus === 'active' && isUserLoggedIn === 'loggedIn') {
        refreshToken();
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [isUserLoggedIn, appStateStatus]);

  const AuthenticationNavigator = () => (
    <AuthenticationStack.Navigator id='AuthenticationStack'>
      <AuthenticationStack.Screen name='SignIn' options={{ headerShown: false }}>
        {(screenProps) => (
          <SignInScreen {...screenProps} setStatus={(status) => setUserLoggedIn(status)} />
        )}
      </AuthenticationStack.Screen>
      <AuthenticationStack.Screen name='SignUp' component={AnalyticsScreen} />
      <AuthenticationStack.Screen name='Home' component={HomeScreen} />
      <AuthenticationStack.Screen name='Support' component={SupportScreen} options={{ presentation: 'modal' }} initialParams={{ signInHelp: true }} />
      <AuthenticationStack.Screen name='Dashboard' component={DashboardScreen} options={{ presentation: 'modal', headerShown: false }} />
    </AuthenticationStack.Navigator>
  );

  if (isUserLoggedIn === 'initializing') {
    return (
      <ActivityIndicator style={{ flex: 1 }} size={'large'}
        color={Colors.light.hereworksLoading} />
    );
  }

  if (isUserLoggedIn === 'unsupportedVersion') return <UpdateAppScreen />;

  // for jwt use && !window.location?.origin?.includes('localhost')
  if (isUserLoggedIn !== 'loggedIn') {
    return (
      <NavigationContainer
        linking={{
          config: LinkingConfiguration.config,
          prefixes: [ExpoLinking.createURL('/')],
          subscribe(listener) {
            const onReceiveURL = ({ url }: { url: string }) => listener(url);

            const eventListenerSubscription = Linking.addEventListener('url', onReceiveURL);

            return () => {
              eventListenerSubscription.remove();
            };
          },
        }}>
        <AuthenticationNavigator />
        <AlertModal modalVisible={authModalVisible} setModalVisible={() => setAuthModalVisible(false)} title={'Not authorized!'} message={'Please log in again'} />
      </NavigationContainer>
    );
  }

  const handleTicketError = (message?: string) => {
    setAlertMessage(message ?? 'Unable to load ticket details');
  };

  const navigateFromPushNotification = (res) => {
    const notificationIdentifier: string | null = res?.notification?.request?.identifier;
    if (notificationIdentifier && notificationIdentifierMemo[notificationIdentifier]) return;
    let ticketId: string | null = null;
    let buildingId: string | null = null;
    let notificationIds: string[] | null = null;
    let category: HappyScoreCategory | null = null;
    if (res) {
      const data = res?.notification?.request.content.data;
      ticketId = data.issueId;
      buildingId = data.buildingId;
      category = data.notificationCategory;
      notificationIds = data.notificationIds;
    }
    if (ticketId) {
      if (buildingId) dispatch(updateBuildingId(buildingId));
      getTicket(ticketId).then((getTicketResponse) => {
        if (!getTicketResponse || getTicketResponse.isError) {
          handleTicketError(getTicketResponse?.errorText ?? 'Unable to load ticket details');
          return;
        }
        if (getTicketResponse.buildingId) {
          dispatch(updateBuildingId(getTicketResponse.buildingId));
        }
        dispatch(updateSelectedTicket({ ...getTicketResponse, pinned: true }));
        navigationRef.navigate(AppScreen.TICKETS as any);
      }).catch((e) => {
        handleTicketError(e?.message);
      }).finally(() => {
        notificationIdentifierMemo[notificationIdentifier] = true;
      });
    } else if (buildingId) {
      dispatch(updateBuildingId(buildingId));
      if (category) {
        navigationRef
          .navigate(getCategoryScreen(category) as any, { openInsights: true, notificationIds });
      } else navigationRef.navigate(AppScreen.HOME as any);
    }
  };

  return (
    <SafeAreaProvider>
      <NavigationContainer
        linking={{
          config: LinkingConfiguration.config,
          prefixes: [ExpoLinking.createURL('/')],
          subscribe(listener) {
            const onReceiveURL = ({ url }: { url: string }) => listener(url);

            const eventListenerSubscription = Linking.addEventListener('url', onReceiveURL);

            navigateFromPushNotification(Notifications.getLastNotificationResponse());

            const subscription = Notifications
              .addNotificationResponseReceivedListener(navigateFromPushNotification);
            return () => {
              eventListenerSubscription.remove();
              subscription.remove();
            };
          },
        }}
        theme={DefaultTheme}
        ref={navigationRef}
        onReady={() => {
          routeNameRef.current = navigationRef.getCurrentRoute().name;
        }}
        onStateChange={async () => {
          // const previousRouteName = routeNameRef.current;
          const currentRouteName = navigationRef.getCurrentRoute().name;

          // if (previousRouteName !== currentRouteName) {
          //   await analytics().logEvent('screen_view', { screen: currentRouteName });
          // }

          routeNameRef.current = currentRouteName;
        }}
      >
        {/* theme={myTheme === 'dark' ? DarkTheme : DefaultTheme}> */}
        <RootNavigator />
        <AlertModal
          modalVisible={ticketErrorModalVisible}
          setModalVisible={setTicketErrorModalVisible}
          title='Error'
          message={ticketErrorMessage}
        />
      </NavigationContainer>
    </SafeAreaProvider>
  );
}

/*
 * A root stack navigator is often used for displaying modals on top of all other content.
 * https://reactnavigation.org/docs/modal
 */
const Stack = createNativeStackNavigator<RootStackParamList>();

function RootNavigator() {
  return (
    <Stack.Navigator id='root-stack'>
      <Stack.Screen name='Root' component={BottomTabNavigator} options={{ headerShown: false }} />
      <Stack.Screen name='Menu' component={MenuSection} options={{ headerShown: false }} />
      <Stack.Screen
        name={AppScreen.MAP}
        component={MapScreenMobile}
        options={{
          title: 'Map',
          headerStyle: { backgroundColor: Colors.light.hereworksBlue500 },
          headerTintColor: '#fff',
          headerTitleStyle: { fontWeight: 'bold' },
          headerBackVisible: true,
          headerBackTitle: 'Back',
        }}
      />
      <Stack.Screen
        name='AssetStack'
        component={AssetManagementScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name={AppScreen.TEMPLATE_MANAGEMENT}
        component={TemplateMgmtScreenMobile}
        options={{ title: 'Template management', headerShown: false }}
      />
      <Stack.Screen name='NotFound' component={NotFoundScreen} options={{ title: 'Oops!' }} />
    </Stack.Navigator>
  );
}

/**
 * A bottom tab navigator displays tab buttons on the bottom of the display to switch screens.
 * https://reactnavigation.org/docs/bottom-tab-navigator
 */
const BottomTab = createBottomTabNavigator<RootTabParamList>();
const MenuStack = createNativeStackNavigator();

const MenuSection = ({ route, navigation }) => {
  const deepScreen = route.params?.screen as string | undefined;
  const deepParams = route.params?.params as Record<string, any> | undefined;

  // 1) consume deep-link once and 2) clear it so it won't re-trigger on next tab focus
  useEffect(() => {
    if (deepScreen) {
      navigation.setParams({ screen: undefined, params: undefined });
    }
  }, [deepScreen, navigation]);

  // helper: attach initialParams only to the matching screen
  const initialParamsFor = (name: string) => (name === deepScreen ? deepParams : undefined);

  return <MenuStack.Navigator id='menu-stack'>
    <MenuStack.Screen
      name='Menu'
      component={MenuScreen}
      options={{ headerShown: false }}
    />
    <MenuStack.Screen
      name={AppScreen.ANALYTICS}
      component={AnalyticsScreen}
      options={{ title: 'Solve Analytics', headerShown: false }}
    />
    <MenuStack.Screen
      name={AppScreen.TICKETS_V2}
      component={TicketsV2}
      options={{ title: 'Tickets V2', headerShown: false }}
    />
    <MenuStack.Screen
      name={AppScreen.USER_MANAGEMENT}
      component={UserMgmtScreen}
      options={{ title: 'User Management', headerShown: false }}
    />
    <MenuStack.Screen
      name='Map'
      component={MapScreenMobile}
      options={{ title: 'Map', headerShown: false }}
    />
    <MenuStack.Screen name='Support' component={SupportScreen}
      options={() => ({
        presentation: 'modal',
        headerBackVisible: true,
      })} />
    <MenuStack.Screen
      name={AppScreen.NOTIFICATIONS_LOG}
      component={NotificationsLogScreen}
      options={{ title: 'Notifications Log', headerShown: false }}
    />
    <MenuStack.Screen
      name={AppScreen.KNOWLEDGE_CENTER}
      component={KnowledgeCenterScreen}
      options={{ title: 'Knowledge center', headerShown: false }}
    />
    <MenuStack.Screen
      name={AppScreen.ASSET_MANAGEMENT}
      component={AssetManagementScreen}
      options={{ title: 'Asset management', headerShown: false }}
    />
    <MenuStack.Screen
      name={AppScreen.TEMPLATE_MANAGEMENT}
      component={TemplateMgmtScreenMobile}
      options={{ title: 'Template management', headerShown: false }}
    />
    <MenuStack.Screen
      name={AppScreen.PROFILE}
      component={ProfileScreen}
      options={{
        title: 'Profile',
        headerShown: false,
      }}
    />
    <MenuStack.Screen
      name={AppScreen.ENERGY}
      component={EnergyScreen}
      options={{
        title: HappyScoreCategoryTitles[HappyScoreCategory.ENERGY],
        headerShown: false,
      }}
    />
    <MenuStack.Screen
      name={getCategoryScreen(HappyScoreCategory.OCCUPANCY)}
      component={OccupantsScreen}
      options={{
        title: HappyScoreCategoryTitles[HappyScoreCategory.OCCUPANCY],
        headerShown: false,
      }}
    />
    <MenuStack.Screen
      name={getCategoryScreen(HappyScoreCategory.INTERNAL_ENVIRONMENT)}
      component={InternalEnvironmentScreen}
      options={{
        title: HappyScoreCategoryTitles[HappyScoreCategory.INTERNAL_ENVIRONMENT],
        headerShown: false,
      }}
    />
    <MenuStack.Screen
      name={getCategoryScreen(HappyScoreCategory.EXTERNAL_ENVIRONMENT)}
      component={ExternalEnvironmentScreen}
      options={{
        title: HappyScoreCategoryTitles[HappyScoreCategory.EXTERNAL_ENVIRONMENT],
        headerShown: false,
      }}
      initialParams={initialParamsFor(getCategoryScreen(HappyScoreCategory.EXTERNAL_ENVIRONMENT))}
    />
    <MenuStack.Screen
      name={getCategoryScreen(HappyScoreCategory.AMENITIES)}
      component={AmenitiesScreen}
      options={{
        title: HappyScoreCategoryTitles[HappyScoreCategory.AMENITIES],
        headerShown: false,
      }}
    />
    <MenuStack.Screen
      name={getCategoryScreen(HappyScoreCategory.ERGONOMICS)}
      component={ErgonomicsScreen}
      options={{
        title: HappyScoreCategoryTitles[HappyScoreCategory.ERGONOMICS],
        headerShown: false,
      }}
    />
    <MenuStack.Screen
      name={getCategoryScreen(HappyScoreCategory.SATISFACTION)}
      component={SatisfactionScreen}
      options={{
        title: HappyScoreCategoryTitles[HappyScoreCategory.SATISFACTION],
        headerShown: false,
      }}
    />
    <MenuStack.Screen
      name={getCategoryScreen(HappyScoreCategory.IT)}
      component={ITScreen}
      options={{
        title: HappyScoreCategoryTitles[HappyScoreCategory.IT],
        headerShown: false,
      }}
    />
    <MenuStack.Screen
      name={AppScreen.HAPPY_CHAT_V2}
      component={ChatbotScreenV2}
      options={{
        headerStyle: { backgroundColor: Colors.light.hereworksBlue500 },
        headerTintColor: '#fff',
        title: 'Insights Agent',
      }} />
    <MenuStack.Screen
      name={AppScreen.GRAPH_PROTOTYPE}
      component={GraphPrototype}
      options={{
        headerStyle: { backgroundColor: '#123' }, headerTintColor: '#fff',
      }} />
    <MenuStack.Screen
      name={AppScreen.TEST_WEB_VIEW}
      component={TestWebView}
      options={{
        headerStyle: { backgroundColor: '#123' }, headerTintColor: '#fff',
      }} />
    <MenuStack.Screen
      name="PDFViewer"
      component={PDFViewerScreen}
      options={{
        title: 'PDF Viewer',
        headerStyle: { backgroundColor: Colors.light.hereworksBlue500 },
        headerTintColor: '#fff',
      }} />
    <MenuStack.Screen
      name={AppScreen.LOCATION_MANAGEMENT}
      component={LocationManagementScreen}
      options={{ title: 'Location Management', headerShown: false }}
    />
    <MenuStack.Screen
      name={AppScreen.TEMPLATE_MANAGEMENTV2}
      component={TemplateMgmtScreenV2}
      options={{ title: 'Template Management V2', headerShown: false }}
    />
  </MenuStack.Navigator>;
};

function BottomTabNavigator() {
  const insets = useSafeAreaInsets();
  const tabBarBottomInset = Platform.OS === 'android'
    ? Math.max(insets.bottom, 12)
    : insets.bottom;

  const { permissions, isAdmin } = useSelector((state: {
    user: {
      permissions: string[], isAdmin: boolean
    }
  }) => state.user);

  const hasTicketsPermission = isAdmin || permissions.includes(UserPermission.TICKETS_READ);
  const HomeTabs = () => (
    <BottomTab.Navigator
      id="home-tabs"
      initialRouteName={happyEnabled ? AppScreen.HOME : AppScreen.TICKETS}
      safeAreaInsets={{ bottom: tabBarBottomInset, top: 0 }}
      screenOptions={{
        header: () => (haveInternet ? null
          : <Animatable.View animation={'slideInDown'} style={{
            backgroundColor: '#fff', position: 'absolute', top: 0, left: 0, right: 0, padding: 54, flex: 1, alignItems: 'center', justifyContent: 'center',
          }}>
            <WarningIcon color={Colors.light.hereworksBlue500} />
            <Text style={{ color: Colors.light.hereworksBlue500, fontFamily: 'Poppins_500Medium', marginTop: 14 }}>You appear to be offline</Text>
          </Animatable.View>),
        tabBarActiveTintColor: Colors.light.hereworksDarkBlue,
        tabBarInactiveTintColor: Colors.light.hereworksBlue400,
        tabBarStyle: {
          height: 78 + tabBarBottomInset,
          paddingBottom: tabBarBottomInset,
          alignContent: 'center',
          justifyContent: 'center',
          borderTopColor: Colors.light.hereworksBlue100,
        },
        tabBarLabelPosition: 'below-icon',
        tabBarItemStyle: {
          height: 50,
          paddingTop: 8,
        },
        tabBarLabelStyle: { fontFamily: 'Roboto_400Regular', marginTop: 6 },
      }}
    >
      {happyEnabled && <BottomTab.Screen
        name={AppScreen.HOME}
        component={HomeScreen}
        initialParams={{ isMobile: true }}
        options={{
          title: 'Home',
          tabBarIcon: ({ focused, color }) => {
            if (focused) {
              return <HomeIcon fill={color} color={color} />;
            }

            return <HomeIcon color={color} />;
          },
        }}
      />}
      {hasTicketsPermission && <BottomTab.Screen
        name={AppScreen.TICKETS}
        component={TicketsScreen}
        options={{
          title: 'Tickets',
          tabBarIcon: ({ focused, color }) => {
            if (focused) {
              return <FlagIcon fill={color} color={color} />;
            }

            return <FlagIcon color={color} />;
          },
        }}
      />}
      {!happyEnabled && <BottomTab.Screen
        name={AppScreen.ASSET_MANAGEMENT}
        component={AssetManagementScreen}
        options={{
          title: 'Assets',
          tabBarIcon: ({ focused, color }) => {
            if (focused) {
              return <GridIcon color={color} />;
            }

            return <GridIcon color={Colors.light.hereworksBlue400} />;
          },
        }}
      />}
      {!happyEnabled && <BottomTab.Screen
        name={AppScreen.ANALYTICS}
        component={AnalyticsScreen}
        options={{
          title: 'Analytics',
          tabBarIcon: ({ focused, color }) => {
            if (focused) {
              return <SolveIcon24 color={color} />;
            }

            return <SolveIcon24 color={Colors.light.hereworksBlue400} />;
          },
        }}
      />}
      <BottomTab.Screen
        name='Notifications'
        component={NotificationsCard}
        options={{
          title: 'Notifications',
          tabBarIcon: ({ focused, color }) => <View>
            <BellIcon fill={focused ? color : '#FFF'} color={color} />
            {(loading || notificationsCount > 0) && <View
              style={[
                styles.notificationCountView,
                notificationsCount > 99 ? { width: 26, right: -10 } : {},
              ]}
            >
              {loading ? <ActivityIndicator
                size={'small'}
                color={'#fff'}
                animating={loading}
                style={{ transform: [{ scale: 0.5 }] }}
              /> : <Text style={styles.notificationText}>{notificationsCount}</Text>}
            </View>}
          </View>,
        }}
      />
      <BottomTab.Screen
        name='MenuStack'
        component={MenuSection}
        options={{
          title: 'Menu',
          tabBarIcon: ({ color }) => <MenuIcon fill={color} />,
        }}
      />
    </BottomTab.Navigator>
  );

  const window = Dimensions.get('window');
  const [dimensions, setDimensions] = useState({ window });
  const [collapsed, setCollapsed] = useState<boolean>(false);

  const happyEnabled = useSelector((state: { happyEnabled: { enabled: boolean } }) => state
    .happyEnabled.enabled);

  const notificationsCount: number = useSelector(
    (state: { selectedBuilding: { badgeCount: number } }) => state.selectedBuilding.badgeCount,
  );

  const [loading, setLoading] = useState(true);
  const [haveInternet, setHaveInternet] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    updateNotificationCount();
    const appStateListener = AppState.addEventListener('change', updateNotificationCount);
    return appStateListener.remove;
  }, []);

  useEffect(() => {
    const subscription = addNetworkStateListener(({ isInternetReachable }) => {
      setHaveInternet(isInternetReachable);
    });
    return subscription.remove();
  }, []);

  const updateNotificationCount: () => void = () => {
    setLoading(true);
    GetUnreadNotificationsCount().then((res) => {
      if (!res) return;
      dispatch(updateBadgeCount(res.count ?? 0));
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    Notifications.requestPermissionsAsync({
      ios: {
        allowAlert: true,
        allowBadge: true,
        allowSound: true,
      },
    }).then((value: Notifications.PermissionResponse) => {
      if (value.granted) Notifications.setBadgeCountAsync(notificationsCount);
    });
  }, [notificationsCount]);

  useEffect(() => {
    const subscription: any = Dimensions.addEventListener(
      'change',
      // eslint-disable-next-line no-shadow
      ({ window }) => {
        setDimensions({ window });
      },
    );
    return subscription?.remove;
  }, []);

  if (dimensions.window?.width > 500 && dimensions.window.height > 500 && Device.brand === null) {
    const Drawer = createDrawerNavigator();

    const initialised: boolean = typeof happyEnabled === 'boolean';

    if (!initialised) {
      return null;
    }

    return (
      <Drawer.Navigator
        id="desktop-drawer"
        initialRouteName={happyEnabled ? 'Home' : 'Tickets'}
        defaultStatus={'open'}
        screenOptions={{
          drawerType: 'permanent',
          drawerStyle: {
            maxWidth: collapsed ? 80 : 252,
            borderRightWidth: 0,
          },
          header: () => <CustomHeader collapsed={collapsed} />,
          drawerItemStyle: { flex: 0 },
          headerStyle: {
            borderBottomWidth: 0,
            backgroundColor: '#FFFFFF',
          },
        }}
        drawerContent={(props) => (
          <CustomDrawer
            toggleDarkMode={() => {
              // AsyncStorage.setItem('theme', 'light').then(() => {
              //   updateTheme({ DefaultTheme });
              // });
            }}
            collapsed={collapsed}
            setCollapsed={() => setCollapsed(!collapsed)}
            {...props}
          />
        )}
      >
        {happyEnabled && <Drawer.Screen
          name={AppScreen.HOME}
          component={HomeScreen}
        />}
        <Drawer.Screen
          name={AppScreen.TICKETS}
          component={TicketsScreen}
        />
        <Drawer.Screen
          name={AppScreen.TICKETS_V2}
          component={TicketsV2}
        />
        <Drawer.Screen
          name={AppScreen.TEMPLATE_MANAGEMENTV2}
          options={{ title: 'Template management V2' }}
          component={TemplateMgmtScreenV2}
        />
        <Drawer.Screen
          name={AppScreen.ASSET_MANAGEMENT}
          options={{ title: 'Asset management' }}
          component={AssetManagementScreen}
        />
        <Drawer.Screen
          name={AppScreen.LOCATION_MANAGEMENT}
          options={{ title: 'Location management' }}
          component={LocationManagementScreen}
        />
        <Drawer.Screen
          name={AppScreen.NOTIFICATIONS_LOG}
          options={{ title: 'Notifications log' }}
          component={NotificationsLogScreen}
        />
        <Drawer.Screen
          name={AppScreen.ANALYTICS}
          component={AnalyticsScreen}
          options={{ title: 'Solve analytics' }}
        />
        <Drawer.Screen
          name={AppScreen.SATISFACTION}
          component={SatisfactionScreen}
          options={{ title: HappyScoreCategoryTitles[HappyScoreCategory.SATISFACTION] }}
        />
        <Drawer.Screen
          name={AppScreen.OCCUPANCY}
          component={OccupantsScreen}
          options={{ title: HappyScoreCategoryTitles[HappyScoreCategory.OCCUPANCY] }}
        />
        <Drawer.Screen
          name='WidgetShowcase'
          component={WidgetShowcaseScreen}
        />
        <Drawer.Screen
          name='GraphPrototype'
          component={GraphPrototype}
          options={{
            headerStyle: { backgroundColor: '#123' },
          }}
        />
        <Drawer.Screen
          name='TestWebView'
          component={TestWebView}
          options={{
            headerStyle: { backgroundColor: '#123' },
          }}
        />
        <Drawer.Screen
          name={AppScreen.AMENITIES}
          component={AmenitiesScreen}
          options={{ title: HappyScoreCategoryTitles[HappyScoreCategory.AMENITIES] }}
        />
        <Drawer.Screen
          name={AppScreen.ERGONOMICS}
          component={ErgonomicsScreen}
          options={{ title: HappyScoreCategoryTitles[HappyScoreCategory.ERGONOMICS] }}
        />
        <Drawer.Screen
          name={AppScreen.PDFViewer}
          component={PDFViewerScreen}
          options={{ title: 'PDF Viewer' }}
        />
        <Drawer.Screen
          name={AppScreen.MAP}
          component={MapScreen}
        />
        <Drawer.Screen
          name={AppScreen.IT}
          component={ITScreen}
          options={{ title: HappyScoreCategoryTitles[HappyScoreCategory.IT] }}
        />
        <Drawer.Screen
          name={AppScreen.ENERGY}
          component={EnergyScreen}
          options={{ title: HappyScoreCategoryTitles[HappyScoreCategory.ENERGY] }}
        />
        <Drawer.Screen
          name={AppScreen.EXTERNAL_ENVIRONMENT}
          component={ExternalEnvironmentScreen}
          options={{ title: HappyScoreCategoryTitles[HappyScoreCategory.EXTERNAL_ENVIRONMENT] }}
        />
        <Drawer.Screen
          name={AppScreen.INTERNAL_ENVIRONMENT}
          component={InternalEnvironmentScreen}
          options={{ title: HappyScoreCategoryTitles[HappyScoreCategory.INTERNAL_ENVIRONMENT] }}
        />
        <Drawer.Screen
          name={AppScreen.HAPPY_CHAT}
          component={ChatbotScreen}
        />
        <Drawer.Screen
          name={AppScreen.HAPPY_CHAT_V2}
          component={ChatbotScreenV2}
        />
        <Drawer.Screen
          name={AppScreen.USER_MANAGEMENT}
          component={UserMgmtScreen}
        />
        <Drawer.Screen
          name={`${AppScreen.USER_MANAGEMENT}V2`}
          component={UserMgmtScreenV2}
        />
        <Drawer.Screen
          name={AppScreen.PROFILE}
          component={ProfileScreen}
        />
        <Drawer.Screen
          name={AppScreen.KNOWLEDGE_CENTER}
          component={KnowledgeCenterScreen}
        />
        <Drawer.Screen
          name={AppScreen.SUPPORT}
          component={SupportScreen}
        />
      </Drawer.Navigator>
    );
  }

  return HomeTabs();
}

// function toggleDarkMode() {
//   if(this.colorScheme === 'dark'){
//     this.colorScheme = 'light';
//   } else {
//     this.colorScheme = 'dark';
//   }

// }

const styles = StyleSheet.create({
  notificationCountView: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: 'red',
    position: 'absolute',
    top: -4,
    right: -2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationText: {
    color: '#fff',
    fontFamily: 'Roboto_500Medium',
    fontSize: 11,
    textAlign: 'center',
  },
});
